/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import { getSettings, useSettings } from '@/data/Settings';
import { DEFAULT_LOCATION, STLOC_ALL_STORES_DEF_QUERY } from '../constants/storeLocator';
import { ID } from '@/data/types/Basic';
import { StoreAttribute, StoreDetails, StoresByCity } from '@/data/types/Store';
import { error as logError } from '@/data/utils/loggerUtil';
import { transactionsStoreLocator } from 'integration/generated/transactions';
import { StorelocatorStorelocator } from 'integration/generated/transactions/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { GetServerSidePropsContext } from 'next';
import useSWR, { unstable_serialize } from 'swr';
import { getUser } from '../User';
import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { shrink, expand as expandProps } from '@/data/utils/keyUtil';
import { getLocalization } from '@/data/Localization-Server';
import { getChildContentItems } from '@/data/utils/getChildContentItems';
import { getContentRecommendation } from '@/data/Content/ContentRecommendation-Server';
import { ContentProps } from '@/data/types/ContentProps';
import { STORE_ID } from '../config/STORE_ID';
import { Translation } from 'integration/generated/translations';
import { useLocalization } from '@/data/Localization';
import { createBasicStoreDetails, getCityURLFast } from '../utils/storeUtil';

const DATA_KEY = 'DATA_KEY_STORE_LOCATOR_PAGE';

const dataMap = (data: StorelocatorStorelocator, storeLocatorRoute?: string): {storesByCity:StoresByCity, cityURLs:Record<string,string>} => {
	let store: StoreDetails;
	let stores: StoreDetails[] = [];
	let storesByCity: StoresByCity = {};
	let cityURLs:Record<string,string>={}
	data?.PhysicalStore?.forEach((ps) => {
		store = createBasicStoreDetails(ps, storeLocatorRoute);
		if(store?.city){
			if (storesByCity[store?.city]) {
				stores = storesByCity[store.city];
				stores.push(store);
				storesByCity[store.city] = stores;
			} else{
				storesByCity[store.city] = [store];
			}
			if(!cityURLs[store.city])cityURLs[store.city]=getCityURLFast(ps.Attribute as StoreAttribute[], storeLocatorRoute??"")
		}
	})
	return {
		storesByCity,
		cityURLs
	};
};

const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
		async (
			props: {
				storeId: string;
				latitude: string;
				longitude: string;
				query: {
					[key: string]: string | boolean | ID[] | number;
				}
			},
			params: RequestParams
		): Promise<any | undefined> => {

			try {
				const { storeId, latitude, longitude, query } = props
				const data = await transactionsStoreLocator(pub).storeLocatorFindStores(
					storeId,
					latitude,
					longitude,
					query,
					params
				);
				return data;
			} catch (error) {
				logError(context?.req, 'StoreLocator: fetcher: error: %o', error);
			}
		};

export const getStoreLocatorPage = async ({ cache, id, context, properties }: ContentProps) => {
	const user = await getUser(cache, context);
	const settings = await getSettings(cache, context);
	await Promise.all([
		getLocalization(cache, context.locale || 'nl-NL', 'StoreLocator'),
		...getChildContentItems(id).map((contentProperties) =>
			getContentRecommendation({
				cache,
				id,
				context,
				properties: { ...properties, ...contentProperties },
			})
		)
	]);
	const { StoreLocator } = await getLocalization(cache, context.locale || 'en-US', 'Routes');
	const storeLocatorRoute = (StoreLocator as Translation)?.route as string;

	const props = {
		storeId: settings?.storeId ?? STORE_ID,
		latitude: String(DEFAULT_LOCATION.lat),
		longitude: String(DEFAULT_LOCATION.lng),
		query: STLOC_ALL_STORES_DEF_QUERY
	};

	const cacheScope = getServerCacheScope(context, user.context);
	const key = unstable_serialize([shrink(props), DATA_KEY]);
	const value = cache.get(key, cacheScope) || dataMap(await fetcher(false, context)(props, {}), storeLocatorRoute);
	cache.set(key, Promise.resolve(value), cacheScope);
	return value;
}

export const useStoreLocatorPage = () => {
	const { settings } = useSettings();
	const { StoreLocator: storeLocatorRoute } = useLocalization('Routes');
	const { data, isLoading } = useSWR(
		DEFAULT_LOCATION?.lat ? [
			shrink({
				storeId: settings?.storeId ?? STORE_ID,
				latitude: String(DEFAULT_LOCATION.lat),
				longitude: String(DEFAULT_LOCATION.lng),
				query: STLOC_ALL_STORES_DEF_QUERY
			}),
			DATA_KEY,
		] : null
		,
		async ([props]) => dataMap(await fetcher(true)(expandProps(props), {}), storeLocatorRoute?.route.t())
	);
	return {
		storesByCity: data?.storesByCity??null,
		cityURLs:data?.cityURLs??null,
		isLoading
	}
};